/* eslint-disable jsx-a11y/alt-text */
import produce from 'immer';
import { useEffect, useMemo, useRef } from 'react';
import Paragraph from '@amf/shared/components/content/Paragraph';
import Image from '@amf/shared/components/content/Image';
import Header from '@amf/shared/components/content/Header';
import Link from '@amf/shared/components/content/Link';
import List from '@amf/shared/components/content/List';
import Delimiter from '@amf/shared/components/content/Delimiter';
import { isValidURL } from '@amf/shared/utils/links';

import Alert from './Alert';
import Table from './Table';
import Attachment from './Attachment';
import Embed from './Embed';
import Gallery from './Gallery';
import Raw from './Raw';

export interface ContentData {
  time: number;
  version: string;
  blocks: ContentDataBlock[];
}

export interface ContentDataFile {
  extension: string;
  name: string;
  size: number;
  url: string;
}

export interface ContentDataBlock {
  type: string;
}

export interface ContentDataBlockGroup extends ContentDataBlock {
  children?: ContentDataBlock[];
}

export interface ContentDataBlockText extends ContentDataBlock {
  data: { text: string };
}

export interface ContentDataBlockHeader extends ContentDataBlock {
  data: { text: string; level: number };
}

export interface ContentDataBlockList extends ContentDataBlock {
  data: { items: string[]; style: 'unordered' | 'ordered' };
}

export interface ContentDataBlockImage extends ContentDataBlock {
  data: {
    caption: string;
    file: ContentDataFile;
    stretched: boolean;
    withBackground: boolean;
    withBorder: boolean;
  };
}

export interface ContentDataBlockLink extends ContentDataBlock {
  data: {
    link: string;
    meta: {
      title?: string;
      description?: string;
      image?: {
        url: string;
      };
    };
  };
}

export interface ContentDataBlockDelimiter extends ContentDataBlock {}

export interface ContentDataBlockAlert extends ContentDataBlock {
  data: {
    title: string;
    message: string;
  };
}

export interface ContentDataBlockTable extends ContentDataBlock {
  data: {
    content: string[][];
  };
}

export interface ContentDataBlockAttachment extends ContentDataBlock {
  data: {
    file: ContentDataFile;
    title: string;
  };
}

export interface ContentDataBlockEmbed extends ContentDataBlock {
  data: {
    caption: string;
    embed: string;
    width: number;
    height: number;
    service: 'youtube' | 'instagram' | 'twitter';
    source: string;
  };
}

export interface ContentDataBlockRaw extends ContentDataBlock {
  data: {
    html: string;
  };
}

export function parseContent(data: string): ContentData | undefined {
  let content: ContentData | undefined;
  try {
    content = JSON.parse(data) as ContentData;
  } catch (e) {
    console.error(e);
  }

  return content;
}

interface ContentProps {
  content: string;
}

export default function Content({ content }: ContentProps) {
  const ref = useRef<HTMLDivElement>(null);
  const data = parseContent(content);
  const groups = useMemo(
    () =>
      data?.blocks.reduce((blocks: ContentDataBlockGroup[], block: ContentDataBlock) => {
        const isImage = block.type === 'image';
        if (!isImage) {
          return produce(blocks, draftState => {
            draftState.push({ ...block });
          });
        }

        const beforeType = blocks[blocks.length - 1] && blocks[blocks.length - 1].type;
        if (beforeType !== 'image') {
          return produce(blocks, draftState => {
            draftState.push({ ...block, children: [{ ...block }] });
          });
        }

        blocks = produce(blocks, draftState => {
          draftState[blocks.length - 1].children?.push({ ...block });
        });

        return blocks;
      }, []),
    [data],
  );

  useEffect(() => {
    const elements = ref.current?.querySelectorAll('a');
    elements.forEach(element => {
      const href = element.getAttribute('href');
      if (isValidURL(href)) {
        element.setAttribute('target', '_blank');
      } else {
        element.removeAttribute('target');
      }
    });
  }, [groups]);

  return (
    <div ref={ref} className='Content'>
      {groups?.map((block, key) => {
        if (block.type === 'paragraph') {
          return <Paragraph key={key} block={block as ContentDataBlockText} />;
        }

        if (block.type === 'image' && block.children?.length === 1) {
          return <Image key={key} block={block as ContentDataBlockImage} />;
        }

        if (block.type === 'image' && (block.children?.length ?? 0) > 1) {
          return <Gallery key={key} blocks={block.children as ContentDataBlockImage[]} />;
        }

        if (block.type === 'header') {
          return <Header key={key} block={block as ContentDataBlockHeader} />;
        }

        if (block.type === 'linkTool') {
          return <Link key={key} block={block as ContentDataBlockLink} />;
        }

        if (block.type === 'list') {
          return <List key={key} block={block as ContentDataBlockList} />;
        }

        if (block.type === 'delimiter') {
          return <Delimiter key={key} block={block as ContentDataBlockDelimiter} />;
        }

        if (block.type === 'warning') {
          return <Alert key={key} block={block as ContentDataBlockAlert} />;
        }

        if (block.type === 'table') {
          return <Table key={key} block={block as ContentDataBlockTable} />;
        }

        if (block.type === 'attaches') {
          return <Attachment key={key} block={block as ContentDataBlockAttachment} />;
        }

        if (block.type === 'embed') {
          return <Embed key={key} block={block as ContentDataBlockEmbed} />;
        }

        if (block.type === 'raw') {
          return <Raw key={key} block={block as ContentDataBlockRaw} />;
        }

        return null;
      })}
    </div>
  );
}
